import { z } from 'zod'
import { AuthCodeValue } from './auth.ts'
import { HouseholdId, UserId } from './ids.ts'
import { User } from './user.ts'

// PATCH /account. All-optional-plus-refine, matching TodoUpdateBody — one
// field today, but the shape every other PATCH body here uses.
export const AccountUpdateBody = z
	.object({
		displayName: z.string().min(1).max(100).optional(),
	})
	.refine((b) => b.displayName !== undefined, {
		message: 'displayName must be provided',
	})
export type AccountUpdateBody = z.infer<typeof AccountUpdateBody>

export const AccountUpdateResponse = z.object({ user: User })
export type AccountUpdateResponse = z.infer<typeof AccountUpdateResponse>

// Changing your sign-in address is the same two-step as signing in: request a
// code to the new address, then verify it. The session keys on userId, so it
// survives the change untouched on every device.
export const EmailChangeRequestBody = z.object({ email: z.email() })
export type EmailChangeRequestBody = z.infer<typeof EmailChangeRequestBody>

export const EmailChangeRequestResponse = z.object({ ok: z.literal(true) })
export type EmailChangeRequestResponse = z.infer<
	typeof EmailChangeRequestResponse
>

export const EmailChangeVerifyBody = z.object({
	email: z.email(),
	code: AuthCodeValue,
})
export type EmailChangeVerifyBody = z.infer<typeof EmailChangeVerifyBody>

export const EmailChangeVerifyResponse = z.object({ user: User })
export type EmailChangeVerifyResponse = z.infer<
	typeof EmailChangeVerifyResponse
>

export const AccountDeleteResponse = z.object({ ok: z.literal(true) })
export type AccountDeleteResponse = z.infer<typeof AccountDeleteResponse>

// --- Export ----------------------------------------------------------------

export const ExportStatus = z.enum(['pending', 'ready', 'failed'])
export type ExportStatus = z.infer<typeof ExportStatus>

// One live export per user, at HOUSEHOLD#<hid> / EXPORT#<userId>. The S3 key
// is deliberately absent — it never leaves the server; the client downloads
// through our own authed route. The three nullable fields fill in together
// when the worker finishes. `expiresAt` is an ISO instant (not the epoch
// seconds a DDB `ttl` wants) because the client renders it as a clock time.
export const ExportJob = z.object({
	householdId: HouseholdId,
	userId: UserId,
	status: ExportStatus,
	requestedAt: z.iso.datetime(),
	filename: z.string().nullable().default(null),
	sizeBytes: z.number().int().min(0).nullable().default(null),
	expiresAt: z.iso.datetime().nullable().default(null),
})
export type ExportJob = z.infer<typeof ExportJob>

export const ExportJobResponse = z.object({ job: ExportJob.nullable() })
export type ExportJobResponse = z.infer<typeof ExportJobResponse>

// GET /account/export/download?format=json — the presigned S3 URL the plain
// route would redirect to. For the iOS build, which can't follow an authed link.
export const ExportLinkResponse = z.object({ url: z.url() })
export type ExportLinkResponse = z.infer<typeof ExportLinkResponse>
